import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  InventoryCoinItem,
  InventoryMetaStats,
  getUserInventoryV2,
  toggleCoinLock,
  setFilteredCoinsLock,
  bulkDepositSellFiltered,
  depositOneCoin,
  sellOneCoin,
  calculateAllowanceAmount
} from '../lib/gameEngine';
import { getCoinImageUrl } from '../lib/imageUrl';
import { getFaceValue } from '../lib/pricing';
import { CoinBadges } from './CoinBadges';
import './InventoryMenu.css';

interface InventoryMenuProps {
  onBack: () => void;
  onWalletUpdated: (newWallet: number) => void;
}

interface RarityTier {
  id: string; // 'Over 5M' | 'U5M' | 'U1M' | 'U500k' | 'U100k' | 'NIFC'
  label: string;
  shortLabel: string;
  desc: string;
}

const RARITY_TIERS: RarityTier[] = [
  { id: 'Over 5M', label: 'Over 5M Mintage', shortLabel: '>5M', desc: 'Mintage > 5M' },
  { id: 'U5M', label: 'Under 5M Mintage', shortLabel: 'U5M', desc: '1M to 5M mintage' },
  { id: 'U1M', label: 'Under 1M Mintage', shortLabel: 'U1M', desc: '500k to 1M mintage' },
  { id: 'U500k', label: 'Under 500k Mintage', shortLabel: 'U500k', desc: '100k to 500k mintage' },
  { id: 'U100k', label: 'Under 100k Mintage', shortLabel: 'U100k', desc: 'Under 100k mintage' },
  { id: 'NIFC', label: 'NIFC (0 Mintage)', shortLabel: 'NIFC', desc: 'Set-only collector coins' }
];

interface ConfirmModalState {
  isOpen: boolean;
  type: 'bulk_deposit_sell' | 'bulk_lock' | 'bulk_unlock' | 'single_sell' | 'single_deposit';
  title: string;
  message: React.ReactNode;
  confirmText: string;
  confirmStyle?: 'danger' | 'warning' | 'primary';
  onConfirm: () => Promise<void> | void;
}

export const INVENTORY_DENOMINATIONS = [
  { key: 'all', label: 'All Denominations', shortLabel: 'All' },
  { key: '2euro', label: '2 Euros (2€)', shortLabel: '2€' },
  { key: '1euro', label: '1 Euro (1€)', shortLabel: '1€' },
  { key: '50cent', label: '50 Cents (50c)', shortLabel: '50c' },
  { key: '20cent', label: '20 Cents (20c)', shortLabel: '20c' },
  { key: '10cent', label: '10 Cents (10c)', shortLabel: '10c' },
  { key: '5cent', label: '5 Cents (5c)', shortLabel: '5c' },
  { key: '2cent', label: '2 Cents (2c)', shortLabel: '2c' },
  { key: '1cent', label: '1 Cent (1c)', shortLabel: '1c' }
] as const;

export const InventoryMenu: React.FC<InventoryMenuProps> = ({ onBack, onWalletUpdated }) => {
  const [items, setItems] = useState<InventoryCoinItem[]>([]);
  const [meta, setMeta] = useState<InventoryMetaStats>({
    totalCoins: 0,
    totalDuplicates: 0,
    distinctCoins: 0,
    duplicatesCount: 0,
    lockedCount: 0,
    totalEstValue: 0,
    rarityCounts: {
      'Over 5M': 0,
      'U5M': 0,
      'U1M': 0,
      'U500k': 0,
      'U100k': 0,
      'NIFC': 0
    },
    countryCounts: {},
    denominationCounts: {}
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [actionProcessing, setActionProcessing] = useState<string | null>(null);

  // Filter States
  const [duplicatesOnly, setDuplicatesOnly] = useState<boolean>(false);
  const [lockedOnly, setLockedOnly] = useState<boolean>(false);
  const [minRarityIndex, setMinRarityIndex] = useState<number>(0);
  const [maxRarityIndex, setMaxRarityIndex] = useState<number>(RARITY_TIERS.length - 1);
  const [selectedCountry, setSelectedCountry] = useState<string>('all');
  const [selectedDenomination, setSelectedDenomination] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'mintage_asc' | 'mintage_desc' | 'quantity'>('mintage_asc');
  const [page, setPage] = useState<number>(0);
  const PAGE_SIZE = 100;

  // Modal State
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState | null>(null);

  const selectedRarities = useMemo(() => {
    if (minRarityIndex === 0 && maxRarityIndex === RARITY_TIERS.length - 1) {
      return null;
    }
    const low = Math.min(minRarityIndex, maxRarityIndex);
    const high = Math.max(minRarityIndex, maxRarityIndex);
    return RARITY_TIERS.slice(low, high + 1).map(t => t.id);
  }, [minRarityIndex, maxRarityIndex]);

  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getUserInventoryV2({
        duplicatesOnly,
        lockedOnly,
        rarities: selectedRarities || undefined,
        country: selectedCountry,
        denomination: selectedDenomination,
        sortBy,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE
      });
      setItems(res.items);
      setMeta(res.meta);
    } catch (err) {
      console.error('Failed to load inventory v2:', err);
    } finally {
      setLoading(false);
    }
  }, [duplicatesOnly, lockedOnly, selectedRarities, selectedCountry, selectedDenomination, sortBy, page]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  // Allowance milestone calculations based on total collection face value
  const distinctCoinsCount = meta.distinctCoins;
  const collectionFaceValue = meta.collectionFaceValue ?? (distinctCoinsCount * 2.00);
  const currentAllowance = calculateAllowanceAmount(collectionFaceValue);
  const nextMilestoneValue = (Math.floor(collectionFaceValue / 100) + 1) * 100;
  const valueNeeded = Number((nextMilestoneValue - collectionFaceValue).toFixed(2));
  const nextTierAllowance = calculateAllowanceAmount(nextMilestoneValue);
  const progressInTier = collectionFaceValue % 100;
  const progressPct = Math.min(100, Math.max(0, (progressInTier / 100) * 100));

  // Total distinct coins matching active filters (for pagination and bulk locks)
  const totalFilteredCoins = meta.filteredDistinctCoins ?? meta.distinctCoins;
  const filteredLockedCoinsCount = meta.filteredLockedCount ?? meta.lockedCount;

  // Eligible count and payout for bulk deposit/sell among all filtered unlocked items
  const fallbackEligibleCount = useMemo(() => {
    if (duplicatesOnly) {
      return items
        .filter(it => !it.isLocked && it.hasDuplicates)
        .reduce((sum, it) => sum + it.duplicateCount, 0);
    }
    return items
      .filter(it => !it.isLocked)
      .reduce((sum, it) => sum + it.count, 0);
  }, [items, duplicatesOnly]);

  const fallbackEligiblePayout = useMemo(() => {
    if (duplicatesOnly) {
      return items
        .filter(it => !it.isLocked && it.hasDuplicates)
        .reduce((sum, it) => {
          const isRare = it.isRare ?? (it.coin.mintage === 0 || (it.coin.mintage != null && it.coin.mintage <= 5_000_000) || it.valuation.marketValue > it.valuation.faceValue);
          const unitVal = isRare ? it.valuation.marketValue : (it.valuation.faceValue ?? getFaceValue(it.coin.denomination));
          return sum + unitVal * it.duplicateCount;
        }, 0);
    }
    return items
      .filter(it => !it.isLocked)
      .reduce((sum, it) => {
        const isRare = it.isRare ?? (it.coin.mintage === 0 || (it.coin.mintage != null && it.coin.mintage <= 5_000_000) || it.valuation.marketValue > it.valuation.faceValue);
        const unitVal = isRare ? it.valuation.marketValue : (it.valuation.faceValue ?? getFaceValue(it.coin.denomination));
        return sum + unitVal * it.count;
      }, 0);
  }, [items, duplicatesOnly]);

  const eligibleDepositSellCount = meta.filteredEligibleCount ?? fallbackEligibleCount;
  const eligibleDepositSellPayout = meta.filteredEligiblePayout ?? fallbackEligiblePayout;

  const sortedCountries = useMemo(() => {
    return Object.keys(meta.countryCounts || {}).sort();
  }, [meta.countryCounts]);

  const selectedRarityCount = useMemo(() => {
    const low = Math.min(minRarityIndex, maxRarityIndex);
    const high = Math.max(minRarityIndex, maxRarityIndex);
    if (low === 0 && high === RARITY_TIERS.length - 1) {
      return meta.distinctCoins;
    }
    return RARITY_TIERS.slice(low, high + 1).reduce(
      (sum, tier) => sum + (meta.rarityCounts[tier.id as keyof typeof meta.rarityCounts] || 0),
      0
    );
  }, [minRarityIndex, maxRarityIndex, meta.distinctCoins, meta.rarityCounts]);

  const handleMinRarityChange = (newIdx: number) => {
    setMinRarityIndex(newIdx);
    setPage(0);
    if (newIdx > maxRarityIndex) {
      setMaxRarityIndex(newIdx);
    }
  };

  const handleMaxRarityChange = (newIdx: number) => {
    setMaxRarityIndex(newIdx);
    setPage(0);
    if (newIdx < minRarityIndex) {
      setMinRarityIndex(newIdx);
    }
  };

  const handleResetFilters = () => {
    setDuplicatesOnly(false);
    setLockedOnly(false);
    setMinRarityIndex(0);
    setMaxRarityIndex(RARITY_TIERS.length - 1);
    setSelectedCountry('all');
    setSelectedDenomination('all');
    setSortBy('mintage_asc');
    setPage(0);
  };

  // Lock Actions
  const handleToggleLock = async (coinId: string) => {
    try {
      setActionProcessing(`lock_${coinId}`);
      const res = await toggleCoinLock(coinId);
      if (res.success) {
        setItems(prev =>
          prev.map(it =>
            it.coin.id === coinId ? { ...it, isLocked: Boolean(res.isLocked) } : it
          )
        );
        await loadInventory();
      }
    } finally {
      setActionProcessing(null);
    }
  };

  const executeBulkLock = async () => {
    try {
      setActionProcessing('bulk_lock');
      const res = await setFilteredCoinsLock(true, {
        duplicatesOnly,
        rarities: selectedRarities || undefined,
        country: selectedCountry,
        denomination: selectedDenomination
      });
      if (res.success) {
        await loadInventory();
      }
    } finally {
      setActionProcessing(null);
    }
  };

  const executeBulkUnlock = async () => {
    try {
      setActionProcessing('bulk_unlock');
      const res = await setFilteredCoinsLock(false, {
        duplicatesOnly,
        rarities: selectedRarities || undefined,
        country: selectedCountry,
        denomination: selectedDenomination
      });
      if (res.success) {
        await loadInventory();
      }
    } finally {
      setActionProcessing(null);
    }
  };

  // Deposit / Sell Actions
  const executeDepositOne = async (coinId: string) => {
    try {
      setActionProcessing(coinId);
      const res = await depositOneCoin(coinId);
      if (res.success) {
        onWalletUpdated(res.newWallet);
        await loadInventory();
      }
    } finally {
      setActionProcessing(null);
    }
  };

  const executeSellOne = async (coinId: string) => {
    try {
      setActionProcessing(coinId);
      const res = await sellOneCoin(coinId);
      if (res.success) {
        onWalletUpdated(res.newWallet);
        await loadInventory();
      }
    } finally {
      setActionProcessing(null);
    }
  };

  const executeBulkDepositSell = async () => {
    if (eligibleDepositSellCount === 0) return;
    try {
      setActionProcessing('bulk_deposit_sell');
      const res = await bulkDepositSellFiltered({
        duplicatesOnly,
        rarities: selectedRarities || undefined,
        country: selectedCountry,
        denomination: selectedDenomination
      });
      if (res.success) {
        onWalletUpdated(res.newWallet);
        await loadInventory();
      }
    } finally {
      setActionProcessing(null);
    }
  };

  const getCoinMintageDisplay = (c: any) => {
    const st = c.status || c.mintageFormatted;
    if (st === 'CIRC') return 'CIRC';
    if (st === 'Likely NIFC') return 'Likely NIFC';
    if (st === 'NIFC') return 'NIFC';
    if (c.mintage === 0) return 'Set Only';
    return `${(c.mintage / 1_000_000).toFixed(c.mintage % 1_000_000 === 0 ? 0 : 1)}M (${c.mintage.toLocaleString('en-US')})`;
  };

  // Click Handlers with Confirmation Modals
  const handleSellOneClick = (item: InventoryCoinItem) => {
    const isRareMintage = (item.coin.mintage ?? 0) <= 1_000_000;
    const mintageText = getCoinMintageDisplay(item.coin);

    if (isRareMintage) {
      setConfirmModal({
        isOpen: true,
        type: 'single_sell',
        title: 'Confirm Sale of Rare Coin',
        message: (
          <div className="confirm-modal-desc">
            <p>
              Are you sure you want to sell <strong>1x {item.coin.country} 2€ ({item.coin.year})</strong>?
            </p>
            <div className="confirm-highlight-box">
              <div>Mintage: <strong>{mintageText}</strong></div>
              <div>Market Payout: <strong className="payout-green">+€{item.valuation.marketValue.toFixed(2)}</strong></div>
            </div>
            {item.count === 1 ? (
              <div className="confirm-warning-note danger">
                <strong>Warning:</strong> This is your <strong>ONLY copy</strong>! Selling it will remove this coin from your collection album.
              </div>
            ) : (
              <div className="confirm-warning-note">
                You currently own <strong>{item.count}x</strong>. You will have <strong>{item.count - 1}x</strong> remaining in your collection.
              </div>
            )}
          </div>
        ),
        confirmText: `Confirm Sale (+€${item.valuation.marketValue.toFixed(2)})`,
        confirmStyle: item.count === 1 ? 'danger' : 'primary',
        onConfirm: () => executeSellOne(item.coin.id)
      });
    } else {
      executeSellOne(item.coin.id);
    }
  };

  const handleDepositOneClick = (item: InventoryCoinItem) => {
    const faceVal = item.valuation.faceValue ?? getFaceValue(item.coin.denomination);
    const isRareMintage = (item.coin.mintage ?? 0) <= 1_000_000;
    const mintageText = getCoinMintageDisplay(item.coin);

    if (isRareMintage) {
      setConfirmModal({
        isOpen: true,
        type: 'single_deposit',
        title: 'Confirm Face Value Deposit of Rare Coin',
        message: (
          <div className="confirm-modal-desc">
            <p>
              Are you sure you want to deposit <strong>1x {item.coin.name}</strong> for face value (<strong>€{faceVal.toFixed(2)}</strong>)?
            </p>
            <div className="confirm-highlight-box">
              <div>Mintage: <strong>{mintageText}</strong></div>
              <div>Market Value: <strong>€{item.valuation.marketValue.toFixed(2)}</strong> (Deposit pays €{faceVal.toFixed(2)})</div>
            </div>
            {item.count === 1 && (
              <div className="confirm-warning-note danger">
                <strong>Warning:</strong> This is your <strong>ONLY copy</strong>! Depositing it will remove this coin from your collection album.
              </div>
            )}
          </div>
        ),
        confirmText: `Confirm Deposit (+€${faceVal.toFixed(2)})`,
        confirmStyle: 'danger',
        onConfirm: () => executeDepositOne(item.coin.id)
      });
    } else {
      executeDepositOne(item.coin.id);
    }
  };

  const handleBulkDepositSellClick = () => {
    if (eligibleDepositSellCount === 0) return;
    setConfirmModal({
      isOpen: true,
      type: 'bulk_deposit_sell',
      title: 'Confirm Bulk Deposit & Sale',
      message: (
        <div className="confirm-modal-desc">
          <p>
            You are about to deposit & sell <strong>{eligibleDepositSellCount}</strong> unlocked {duplicatesOnly ? 'duplicate ' : ''}coin{eligibleDepositSellCount === 1 ? '' : 's'} matching your active filters.
          </p>
          <div className="confirm-highlight-box">
            <div>Estimated Total Payout: <strong className="payout-green">+€{eligibleDepositSellPayout.toFixed(2)}</strong></div>
            <div>Common coins (&gt;5M): Deposited for face value each</div>
            <div>Rare coins (≤5M): Sold at market valuation each</div>
          </div>
          <div className="confirm-warning-note">
            <strong>Protected:</strong> All locked coins ({meta.lockedCount}) are skipped and will not be sold or deposited.
          </div>
        </div>
      ),
      confirmText: `Process ${eligibleDepositSellCount} Coins (+€${eligibleDepositSellPayout.toFixed(2)})`,
      confirmStyle: 'primary',
      onConfirm: executeBulkDepositSell
    });
  };

  const handleBulkLockClick = () => {
    if (totalFilteredCoins === 0) return;
    setConfirmModal({
      isOpen: true,
      type: 'bulk_lock',
      title: 'Confirm Lock All Filtered Coins',
      message: (
        <div className="confirm-modal-desc">
          <p>
            Are you sure you want to lock all <strong>{totalFilteredCoins}</strong> coin{totalFilteredCoins === 1 ? '' : 's'} matching your active filter across all pages?
          </p>
          <div className="confirm-warning-note">
            Locked coins cannot be sold or deposited individually or via bulk actions until you unlock them.
          </div>
        </div>
      ),
      confirmText: `Lock All (${totalFilteredCoins} Coins)`,
      confirmStyle: 'warning',
      onConfirm: executeBulkLock
    });
  };

  const handleBulkUnlockClick = () => {
    if (filteredLockedCoinsCount === 0) return;
    setConfirmModal({
      isOpen: true,
      type: 'bulk_unlock',
      title: 'Confirm Unlock All Filtered Coins',
      message: (
        <div className="confirm-modal-desc">
          <p>
            Are you sure you want to unlock <strong>{filteredLockedCoinsCount}</strong> locked coin{filteredLockedCoinsCount === 1 ? '' : 's'} matching your active filter across all pages?
          </p>
          <div className="confirm-warning-note">
            Unlocked coins will become eligible for individual and bulk selling/depositing.
          </div>
        </div>
      ),
      confirmText: `Unlock All (${filteredLockedCoinsCount} Coins)`,
      confirmStyle: 'primary',
      onConfirm: executeBulkUnlock
    });
  };

  return (
    <div className="inventory-menu-container">
      {/* Navigation */}
      <div className="inventory-nav">
        <button className="back-btn" onClick={onBack}>
          ← Back to Main Menu
        </button>
      </div>

      <div className="inventory-header">
        <h2 className="inventory-title">Inventory & Market Sales</h2>
        <div className="inventory-subtitle">
          View your coins, sell finds, & deposit commons
        </div>
      </div>

      {/* Summary Stats & Allowance Progress Card */}
      <div className="inventory-summary-card">
        <div className="summary-stats-row">
          <div className="summary-stat-item">
            Total Coins: <strong>{meta.totalCoins}</strong>
          </div>
          <div className="summary-stat-item">
            Total Duplicates: <strong>{meta.totalDuplicates}</strong>
          </div>
          <div className="summary-stat-item">
            Est. Total Value: <strong className="val-highlight">€{meta.totalEstValue.toFixed(2)}</strong>
          </div>
        </div>

        {/* Collection Size Allowance & Milestone Progress */}
        <div className="inventory-allowance-section">
          <div className="inventory-allowance-text">
            Your collection has <strong>€{collectionFaceValue.toFixed(2)}</strong> face value (<strong>{distinctCoinsCount}</strong> unique {distinctCoinsCount === 1 ? 'coin' : 'coins'}) and earns <strong>€{currentAllowance.toFixed(2)}</strong> every 12 hours. Add <strong>€{valueNeeded.toFixed(2)}</strong> more face value to earn <strong>€{nextTierAllowance.toFixed(2)}</strong>!
          </div>
          <div className="inventory-allowance-progress-track">
            <div
              className="inventory-allowance-progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Enhanced Filters, Sorting & Bulk Actions Bar */}
      <div className="inventory-controls-panel">
        {/* Row 1: Primary Toggles (Duplicates Only, Locked Only) */}
        <div className="filter-primary-toggles-row">
          <button
            className={`filter-pill-btn ${duplicatesOnly ? 'active' : ''}`}
            onClick={() => {
              setDuplicatesOnly(prev => !prev);
              setPage(0);
            }}
          >
            Duplicates Only <span className="pill-badge">{meta.duplicatesCount}</span>
          </button>

          <button
            className={`filter-pill-btn lock-pill ${lockedOnly ? 'active' : ''}`}
            onClick={() => {
              setLockedOnly(prev => !prev);
              setPage(0);
            }}
          >
            Locked Only <span className="pill-badge">{meta.lockedCount}</span>
          </button>

          {(duplicatesOnly || lockedOnly || minRarityIndex > 0 || maxRarityIndex < RARITY_TIERS.length - 1 || selectedCountry !== 'all') && (
            <button className="reset-filters-btn" onClick={handleResetFilters}>
              Reset Filters
            </button>
          )}
        </div>

        {/* Row 2: Dual Rarity Sliders (Minimum Rarity & Maximum Rarity) */}
        <div className="rarity-sliders-grid">
          {/* Minimum Rarity Slider */}
          <div className="rarity-slider-box">
            <div className="rarity-slider-header">
              <div className="rarity-slider-title">
                <span className="slider-label">Minimum Rarity:</span>
                <strong className="rarity-active-label">{RARITY_TIERS[minRarityIndex].shortLabel}</strong>
              </div>
            </div>

            <div className="rarity-slider-track-wrap">
              <input
                type="range"
                className="rarity-range-input"
                min="0"
                max={RARITY_TIERS.length - 1}
                step="1"
                value={minRarityIndex}
                onChange={e => handleMinRarityChange(Number(e.target.value))}
              />
              <div className="rarity-slider-ticks">
                {RARITY_TIERS.map((tier, idx) => {
                  const count = meta.rarityCounts[tier.id as keyof typeof meta.rarityCounts] || 0;
                  const isActive = idx === minRarityIndex;
                  return (
                    <button
                      key={`min_${tier.id}`}
                      type="button"
                      className={`rarity-tick-btn ${isActive ? 'active' : ''}`}
                      onClick={() => handleMinRarityChange(idx)}
                      title={`${tier.shortLabel} (${count} coins)`}
                    >
                      <span className="tick-dot" />
                      <span className="tick-name">{tier.shortLabel}</span>
                      <span className="tick-count">({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Maximum Rarity Slider */}
          <div className="rarity-slider-box">
            <div className="rarity-slider-header">
              <div className="rarity-slider-title">
                <span className="slider-label">Maximum Rarity:</span>
                <strong className="rarity-active-label">{RARITY_TIERS[maxRarityIndex].shortLabel}</strong>
              </div>
            </div>

            <div className="rarity-slider-track-wrap">
              <input
                type="range"
                className="rarity-range-input"
                min="0"
                max={RARITY_TIERS.length - 1}
                step="1"
                value={maxRarityIndex}
                onChange={e => handleMaxRarityChange(Number(e.target.value))}
              />
              <div className="rarity-slider-ticks">
                {RARITY_TIERS.map((tier, idx) => {
                  const count = meta.rarityCounts[tier.id as keyof typeof meta.rarityCounts] || 0;
                  const isActive = idx === maxRarityIndex;
                  return (
                    <button
                      key={`max_${tier.id}`}
                      type="button"
                      className={`rarity-tick-btn ${isActive ? 'active' : ''}`}
                      onClick={() => handleMaxRarityChange(idx)}
                      title={`${tier.shortLabel} (${count} coins)`}
                    >
                      <span className="tick-dot" />
                      <span className="tick-name">{tier.shortLabel}</span>
                      <span className="tick-count">({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Selected Rarity Range Summary Badge */}
        <div className="rarity-range-summary-bar">
          <span>
            Showing rarity: <strong>{RARITY_TIERS[minRarityIndex].shortLabel}</strong> to <strong>{RARITY_TIERS[maxRarityIndex].shortLabel}</strong>
          </span>
          <span className="rarity-range-count-badge">
            <strong>{selectedRarityCount}</strong> coin{selectedRarityCount === 1 ? '' : 's'} included
          </span>
        </div>

        {/* Row 3: Denomination, Country Dropdowns & Order By */}
        <div className="filter-dropdowns-row">
          <div className="control-group">
            <label className="control-label">Denomination:</label>
            <select
              className="inventory-select"
              value={selectedDenomination}
              onChange={e => {
                setSelectedDenomination(e.target.value);
                setPage(0);
              }}
            >
              <option value="all">All Denominations ({meta.distinctCoins})</option>
              {INVENTORY_DENOMINATIONS.filter(d => d.key !== 'all').map(denom => (
                <option key={denom.key} value={denom.key}>
                  {denom.label} ({meta.denominationCounts?.[denom.key] || 0})
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label className="control-label">Country:</label>
            <select
              className="inventory-select"
              value={selectedCountry}
              onChange={e => {
                setSelectedCountry(e.target.value);
                setPage(0);
              }}
            >
              <option value="all">All Countries ({meta.distinctCoins})</option>
              {sortedCountries.map(country => (
                <option key={country} value={country}>
                  {country} ({meta.countryCounts[country] || 0})
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label className="control-label">Order by:</label>
            <select
              className="inventory-select"
              value={sortBy}
              onChange={e => {
                setSortBy(e.target.value as any);
                setPage(0);
              }}
            >
              <option value="mintage_asc">Mintage Ascending</option>
              <option value="mintage_desc">Mintage Descending</option>
              <option value="quantity">Quantity (Most Owned)</option>
            </select>
          </div>
        </div>

        {/* Row 4: Bulk Actions (Right below filter and sort parameters) */}
        <div className="bulk-actions-row">
          <button
            className="bulk-action-btn deposit-sell"
            onClick={handleBulkDepositSellClick}
            disabled={loading || actionProcessing !== null || eligibleDepositSellCount === 0}
            title={
              eligibleDepositSellCount === 0
                ? 'No unlocked coins to deposit/sell in current view'
                : `Deposit commons & sell rares for ${eligibleDepositSellCount} unlocked coins across all matching pages`
            }
          >
            {duplicatesOnly ? 'Deposit/Sell All Duplicates' : 'Deposit/Sell All'} ({eligibleDepositSellCount} Unlocked)
          </button>

          <button
            className="bulk-action-btn lock-all"
            onClick={handleBulkLockClick}
            disabled={loading || actionProcessing !== null || totalFilteredCoins === 0}
            title="Lock all matching coins across all pages"
          >
            Lock All ({totalFilteredCoins})
          </button>

          {filteredLockedCoinsCount > 0 && (
            <button
              className="bulk-action-btn unlock-all"
              onClick={handleBulkUnlockClick}
              disabled={loading || actionProcessing !== null}
              title="Unlock all matching coins across all pages"
            >
              Unlock All ({filteredLockedCoinsCount})
            </button>
          )}
        </div>
      </div>

      {/* Fixed 3-Column Coin Cards Grid */}
      {loading ? (
        <div className="inventory-empty-state">Loading inventory...</div>
      ) : items.length === 0 ? (
        <div className="inventory-empty-state">
          No coins match the selected filters.
        </div>
      ) : (
        <div className="inventory-grid">
          {items.map(item => {
            const isProcessing =
              actionProcessing === item.coin.id || actionProcessing === `lock_${item.coin.id}`;
            const itemStatus = item.coin.status || item.coin.mintageFormatted;
            const mintageText =
              itemStatus === 'CIRC'
                ? 'CIRC'
                : itemStatus === 'Likely NIFC'
                ? 'Likely NIFC'
                : itemStatus === 'NIFC'
                ? 'NIFC'
                : item.coin.mintage === 0
                ? 'Set Only'
                : item.coin.mintage >= 1_000_000
                ? `${(item.coin.mintage / 1_000_000).toFixed(item.coin.mintage % 1_000_000 === 0 ? 0 : 1)}M`
                : item.coin.mintage.toLocaleString('en-US');

            const thumbSrc = getCoinImageUrl(item.coin.imagePath, 'thumbnails');
            const isLocked = Boolean(item.isLocked);

            return (
              <div
                key={item.coin.id}
                className={`coin-inventory-card ${isLocked ? 'locked-card' : ''}`}
              >
                {/* Top Section / Mobile Left */}
                <div className="card-top-row">
                  <div className="card-img-box">
                    {thumbSrc ? (
                      <img
                        src={thumbSrc}
                        alt={`${item.coin.country} ${item.coin.year}`}
                        className="card-coin-img"
                        loading="lazy"
                      />
                    ) : (
                      <div className="card-img-placeholder">2€</div>
                    )}
                  </div>

                  <div className="card-header-info">
                    <div className="card-coin-title">
                      {item.coin.country} {item.coin.year}
                    </div>
                    {item.coin.subject ? (
                      <div className="card-coin-subject" title={item.coin.subject}>
                        {item.coin.subject}
                      </div>
                    ) : (
                      <div className="card-coin-subject">Standard Issue</div>
                    )}
                    <div className="card-badges-row">
                      <CoinBadges coin={item.coin} inline commemorativeLabel="CC" />
                      <span className="qty-badge">Qty: {item.count}x</span>
                      {isLocked && <span className="locked-badge">Locked</span>}
                      <span className="card-mintage-mobile-badge">
                        Mintage: <strong>{mintageText}</strong>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card-divider" />

                {/* Bottom Section / Mobile Right */}
                <div className="card-bottom-section">
                  <div className="card-details-line">
                    <span className="mintage-desktop-wrap">
                      Mintage: <strong className="mintage-val">{mintageText}</strong>
                    </span>
                    <span className="val-num">€{item.valuation.marketValue.toFixed(2)}</span>
                  </div>

                  {(() => {
                    const isRare = item.isRare ?? (item.coin.mintage === 0 || (item.coin.mintage != null && item.coin.mintage <= 5_000_000) || item.valuation.marketValue > item.valuation.faceValue);
                    const faceVal = item.valuation.faceValue ?? getFaceValue(item.coin.denomination);

                    return (
                      <div className="card-actions-row">
                        {isRare ? (
                          <button
                            className="card-action-btn sell"
                            onClick={() => handleSellOneClick(item)}
                            disabled={isLocked || isProcessing || actionProcessing !== null}
                            title={isLocked ? 'Unlock coin first to sell' : undefined}
                          >
                            {isProcessing
                              ? 'Processing...'
                              : isLocked
                              ? 'Locked'
                              : `Sell 1 (+€${item.valuation.marketValue.toFixed(2)})`}
                          </button>
                        ) : (
                          <button
                            className="card-action-btn deposit"
                            onClick={() => handleDepositOneClick(item)}
                            disabled={isLocked || isProcessing || actionProcessing !== null}
                            title={isLocked ? 'Unlock coin first to deposit' : undefined}
                          >
                            {isProcessing
                              ? 'Processing...'
                              : isLocked
                              ? 'Locked'
                              : `Deposit 1 (+€${faceVal.toFixed(2)})`}
                          </button>
                        )}

                        <button
                          className={`card-lock-btn ${isLocked ? 'is-locked' : ''}`}
                          onClick={() => handleToggleLock(item.coin.id)}
                          disabled={isProcessing || actionProcessing !== null}
                          title={isLocked ? 'Unlock coin' : 'Lock coin to prevent depositing/selling'}
                        >
                          {isLocked ? 'Unlock' : 'Lock'}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && items.length > 0 && (
        <div className="inventory-pagination-row">
          <button
            className="inventory-page-btn"
            disabled={page === 0}
            onClick={() => {
              setPage(prev => Math.max(0, prev - 1));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            ← Previous 100
          </button>
          <div className="inventory-page-info">
            Showing <strong>{page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length}</strong> of <strong>{totalFilteredCoins}</strong> coins
          </div>
          <button
            className="inventory-page-btn"
            disabled={items.length < PAGE_SIZE || (page + 1) * PAGE_SIZE >= totalFilteredCoins}
            onClick={() => {
              setPage(prev => prev + 1);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            Next 100 →
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && confirmModal.isOpen && (
        <div className="inventory-modal-backdrop" onClick={() => setConfirmModal(null)}>
          <div className="inventory-modal-content" onClick={e => e.stopPropagation()}>
            <button
              className="inventory-modal-close"
              onClick={() => setConfirmModal(null)}
              title="Close modal"
            >
              &times;
            </button>
            <h3 className="inventory-modal-title">{confirmModal.title}</h3>
            <div className="inventory-modal-body">{confirmModal.message}</div>
            <div className="inventory-modal-actions">
              <button
                className="inventory-modal-btn cancel"
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </button>
              <button
                className={`inventory-modal-btn confirm ${confirmModal.confirmStyle || 'primary'}`}
                onClick={async () => {
                  const action = confirmModal.onConfirm;
                  setConfirmModal(null);
                  await action();
                }}
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
